import React, { useEffect, useState } from "react";
import { useShellId } from "../../VariantB/shellContext";
import '../PhotoDiagnose/PhotoDiagnose.css'
import { useDropzone } from "react-dropzone";
import SubmitButton from "../../../../components/SubmitButton/SubmitButton";
import cameraLogo from '../../../../icons/camera-logo.png'
import xLogo from '../../../../icons/X.png'
import activeLogo from '../../../../icons/Active.svg'
import Significance from "../Significance/Significance";
import * as translator from "../../../../utils/translator";

interface DocumentUploadProps {
  language?: string,
  setFilesCallback: any,
  files: any,
  necessaryFileCount?: [number, number],
  showError?: boolean,
}

const ACCEPTED_MIME_TYPES = "image/jpeg,image/png,application/pdf";
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  language,
  setFilesCallback,
  files,
  necessaryFileCount = [1, 4],
  showError = false,
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: ACCEPTED_MIME_TYPES,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE_BYTES,

    onDropRejected: (rejected_files) => {
      const has_oversized_file = rejected_files.some(
        (rejected_file) => rejected_file.size > MAX_FILE_SIZE_BYTES
      );

      if (has_oversized_file) {
        setUploadError(
          translator.translate(language, "default", "DocumentUpload/max_file_size_warning")
        );
      }
    },

    onDrop: (accepted_files) => {
      if (!accepted_files || accepted_files.length === 0) return;

      setUploadError(null);

      const new_files = files.map((item: any) => item);

      accepted_files.forEach((curr_accepted_file) => {
        const reader = new FileReader();
        reader.onabort = () => {};
        reader.onerror = () => {};
        reader.onload = () => {
          new_files.push({
            local_image: URL.createObjectURL(curr_accepted_file),
            binary_data: reader.result,
            filename: curr_accepted_file.name,
            type: curr_accepted_file.type,
            upload_kind: 'document_upload',
          });

          setFilesCallback(new_files);
        };
        reader.readAsArrayBuffer(curr_accepted_file);
      });
    },
  });

  useEffect(
    () => () => {
      files.forEach((file: any) => URL.revokeObjectURL(file.preview));
    },
    [files]
  );

  const deleteFile = (index: number) => {
    const new_files = files.filter((_file: any, fileIndex: number) => fileIndex !== index);
    setFilesCallback(new_files);
  };

  const isImage = (file: any) =>
    typeof file?.type === 'string' && file.type.startsWith('image/');

  return (
    <div className="questionnaire-photoDiagnose-container">
      <div
        className="questionnaire-photoDiganose-draganddrop-container"
        {...getRootProps()}
      >
        <div className="questionnaire-photoDiganose-draganddrop">
          <input {...getInputProps()} />
          <img className="questionnaire-photoDiagnose-cameralogo" src={cameraLogo} alt="upload document" />
          <p className="questionnaire-photoDiagnose-title">
            {translator.translate(language, "default", "DocumentUpload/drag_and_drop_text")}
          </p>
          <SubmitButton
            id={useShellId("document_upload_submit")}
            buttonType="button"
            isEnabledState={files.length < necessaryFileCount[1]}
            onSubmit={open}
            language={language}
            placeholder={translator.translate(language, "default", "DocumentUpload/drag_and_drop_button_text")}
            margin={"15px 0px"}
            height={'44px'}
            fontsize={"14"}
          />
          <p className="questionnaire-photoDiagnose-subtext">
            {translator.translate(language, "default", "DocumentUpload/drag_and_drop_subtext")}
          </p>

          {uploadError ? (
            <Significance
              parentID={null}
              dangerSignificancesRefsDispatch={null}
              fontWeight={null}
              level="danger"
              text={uploadError}
            />
          ) : null}
        </div>

        {files.length > necessaryFileCount[1] ? (
          <Significance
            parentID={null}
            dangerSignificancesRefsDispatch={null}
            fontWeight={null}
            level="danger"
            text={translator
              .translate(language, "default", "DocumentUpload/max_files_warning")
              ?.replace(":count", necessaryFileCount[1].toString())}
          />
        ) : null}

        {files.map((file: any, index: number) => (
          <div className="questionnaire-photoDiagnose-uploaded-file-container" key={index}>
            {isImage(file) ? (
              <img className="questionnaire-photoDiagnose-uploaded-image" src={file.local_image} alt={file.filename} />
            ) : (
              <img className="questionnaire-photoDiagnose-uploaded-image" src={cameraLogo} alt={file.filename} />
            )}

            <span className="questionnaire-photoDiagnose-uploaded-filename">{file.filename}</span>
            <div className="questionnaire-photoDiagnose-uploaded-logos">
              <img
                className="questionnaire-photoDiagnose-uploaded-cancel"
                src={xLogo}
                onClick={() => deleteFile(index)}
                alt="remove"
              />
              <img className="questionnaire-photoDiagnose-uploaded-activelogo" src={activeLogo} alt="" />
            </div>
          </div>
        ))}

        {files.length > 0 && files.length < necessaryFileCount[0] ? (
          <Significance
            parentID={null}
            dangerSignificancesRefsDispatch={null}
            fontWeight={null}
            level="warning"
            text={translator
              .translate(language, "default", "DocumentUpload/min_files_warning")
              ?.replace(":count", necessaryFileCount[0].toString())}
          />
        ) : null}
        {showError && files.length === 0 ? (
          <Significance
            parentID={null}
            dangerSignificancesRefsDispatch={null}
            fontWeight={null}
            level="danger"
            text={translator
              .translate(language, "default", "DocumentUpload/min_files_warning")
              ?.replace(":count", necessaryFileCount[0].toString())}
          />
        ) : null}
      </div>
    </div>
  );
};

export default DocumentUpload;
